import React, { useEffect, useState, useCallback } from "react";
import Sidebar from "../../../layout/Sidebar";
import Header from "../../../layout/Header";
import Footer from "../../../layout/Footer";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import AdminDataTable from "../../../components/common/AdminDataTable";
import { cityColumns } from "../../../tables/cityColumns";
import { can } from "../../../utils/can";


export default function City() {
  const PAGE_SIZE = 10;
  const API_URL = process.env.REACT_APP_BASE_URL_API || "";
  const token = localStorage.getItem("adminToken");

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      isActive: true,
      website: false,
    },
  });

  const isActive = watch("isActive");

  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [success, setSuccess] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState([]);
  const [state, setState] = useState([]);
  const [city, setCity] = useState([]);
  const [editId, setEditId] = useState(null);

  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [selectedStateFilter, setSelectedStateFilter] = useState("");
  const [filterStateList, setFilterStateList] = useState([]);

  const selectedCountry = watch("country_id");
  const selectedState = watch("state_id");

  // Fetch Country List
  const fetchCountry = async () => {
    try {
      const url = `${API_URL.replace(/\/$/, "")}/api/front/country`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (response.ok && Array.isArray(result.data)) {
        setCountry(result.data);
      } else {
        setCountry([]);
      }
    } catch (err) {
      console.error("Error fetching country:", err);
      setCountry([]);
    }
  };

  // Fetch states for a given country
  const fetchStatesForCountry = useCallback(
    async (countryId) => {
      if (!countryId) {
        setState([]);
        return [];
      }
      try {
        const url = `${API_URL.replace(/\/$/, "")}/api/front/state/${countryId}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await response.json();
        if (response.ok && Array.isArray(result.data)) {
          setState(result.data);
          return result.data;
        } else {
          setState([]);
          return [];
        }
      } catch (err) {
        console.error("Error fetching state:", err);
        setState([]);
        return [];
      }
    },
    [API_URL, token]
  );

  const handleCountryChange = async (e) => {
    const newCountryId = e.target.value;
    setValue("country_id", newCountryId);
    setValue("state_id", "");
    if (newCountryId) {
      await fetchStatesForCountry(newCountryId);
    } else {
      setState([]);
    }
  };

  // Fetch all active states for filter dropdown
  const fetchAllStates = async () => {
    try {
      const url = `${API_URL.replace(/\/$/, "")}/api/front/state`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (response.ok && Array.isArray(result.data)) {
        setFilterStateList(result.data);
      } else {
        setFilterStateList([]);
      }
    } catch (err) {
      console.error("Error fetching states for filter:", err);
      setFilterStateList([]);
    }
  };

  // Fetch City with pagination, search, state filter, sorting
  const fetchCity = async (
    pageNumber = 1,
    pSize = pageSize,
    currentSearch = activeSearch,
    currentState = selectedStateFilter,
    sortB = sortBy,
    sortO = sortOrder
  ) => {
    setListLoading(true);
    try {
      let url = `${API_URL.replace(/\/$/, "")}/api/admin/city?page=${pageNumber}&limit=${pSize}&sortBy=${sortB}&sortOrder=${sortO}`;
      if (currentSearch && currentSearch.trim()) {
        url += `&search=${encodeURIComponent(currentSearch.trim())}`;
      }
      if (currentState) {
        url += `&state_id=${encodeURIComponent(currentState)}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();

      if (response.ok) {
        setCity(Array.isArray(result.data) ? result.data : []);
        const computedTotal = result.totalPages
          ? result.totalPages
          : Math.ceil(
              (result.totalCount || result.total || (Array.isArray(result.data) ? result.data.length : 0)) / pSize
            ) || 1;
        setTotalPages(computedTotal);
      } else {
        setCity([]);
        console.error(result.message || "Failed to fetch city.");
      }
    } catch (err) {
      console.error("Error fetching city:", err);
      setCity([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchCountry();
    fetchAllStates();
  }, []);

  useEffect(() => {
    fetchCity(page, pageSize, activeSearch, selectedStateFilter, sortBy, sortOrder);
  }, [page, pageSize, activeSearch, selectedStateFilter, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (!column) return;
    const newOrder = sortBy === column && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(column);
    setSortOrder(newOrder);
    setPage(1);
    fetchCity(1, pageSize, activeSearch, selectedStateFilter, column, newOrder);
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setActiveSearch(searchInput);
    setPage(1);
    fetchCity(1, pageSize, searchInput, selectedStateFilter);
  };

  const handleStateFilterChange = (e) => {
    const newState = e.target.value;
    setSelectedStateFilter(newState);
    setPage(1);
    fetchCity(1, pageSize, activeSearch, newState);
  };

  const handleSearchReset = () => {
    setSearchInput("");
    setActiveSearch("");
    setSelectedStateFilter("");
    setPage(1);
    fetchCity(1, pageSize, "", "");
  };

  // Form Submit
  const onSubmit = async (data) => {
    if (can("city", "create") === false) {
      alert("You don't have permission.");
      return;
    }

    setSuccess("");
    setErrorMsg("");
    setLoading(true);

    try {
      const url = editId
        ? `${API_URL.replace(/\/$/, "")}/api/admin/city/update/${editId}`
        : `${API_URL.replace(/\/$/, "")}/api/admin/city/create`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          country_id: data.country_id,
          state_id: data.state_id,
          name: data.name,
          status: data.isActive ? "Active" : "Deactive",
          website: data.isActive ? !!data.website : false,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(editId ? "City updated successfully!" : "City added successfully!");
        setErrorMsg("");
        reset();
        setState([]);
        setEditId(null);
        fetchCity(page, pageSize, activeSearch, selectedStateFilter);
      } else {
        setErrorMsg(result.message || "Failed to save city.");
        setSuccess("");
      }
    } catch (err) {
      console.error("Error:", err);
      setErrorMsg("Something went wrong. Please try again.");
      setSuccess("");
    } finally {
      setLoading(false);
    }
  };

  // Edit City
  const handleEdit = async (cityItem) => {
    if (can("city", "edit") === false) {
      alert("You don't have permission.");
      return;
    }

    let countryId =
      cityItem?.country_id?._id ||
      cityItem?.country_id ||
      cityItem?.state_id?.country_id?._id ||
      cityItem?.state_id?.country_id ||
      "";
    const stateId = String(cityItem?.state_id?._id || cityItem?.state_id || "");

    if (!countryId && stateId && Array.isArray(filterStateList)) {
      const foundState = filterStateList.find((s) => String(s._id) === stateId);
      if (foundState) {
        countryId = foundState.country_id?._id || foundState.country_id || "";
      }
    }
    countryId = String(countryId || "");

    setEditId(cityItem?._id || null);
    setValue("name", cityItem?.name || "");
    setValue("isActive", cityItem?.status === "Active");
    setValue("website", !!cityItem?.website);

    if (countryId) {
      setValue("country_id", countryId);
      const stateList = await fetchStatesForCountry(countryId);
      if (stateId && stateList && stateList.length > 0) {
        setValue("state_id", stateId);
        setTimeout(() => {
          setValue("state_id", stateId);
        }, 50);
      }
    } else if (stateId) {
      setValue("state_id", stateId);
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Delete City
  const handleDelete = async (id) => {
    if (can("city", "delete") === false) {
      alert("You don't have permission.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this city?")) return;
    try {
      const response = await fetch(`${API_URL.replace(/\/$/, "")}/api/admin/city/delete/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (response.ok) {
        setSuccess("City deleted successfully!");
        fetchCity(page, pageSize, activeSearch, selectedStateFilter);
      } else {
        setSuccess(result.message || "Failed to delete city.");
      }
    } catch (err) {
      console.error(err);
      setSuccess("Something went wrong!");
    }
  };

  const handleStatusToggle = async (item) => {
    if (can("city", "edit") === false) {
      alert("You don't have permission.");
      return;
    }

    const newStatus = item.status === "Active" ? "Deactive" : "Active";

    try {
      const response = await fetch(
        `${API_URL.replace(/\/$/, "")}/api/admin/city/update/${item._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: newStatus,
            ...(newStatus === "Deactive" ? { website: false } : {}),
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setCity((prev) =>
          prev.map((row) =>
            row._id === item._id
              ? {
                  ...row,
                  status: newStatus,
                  website: newStatus === "Deactive" ? false : row.website,
                }
              : row
          )
        );
      } else {
        setSuccess(result.message || "Failed to update status.");
      }
    } catch (err) {
      console.error(err);
      setSuccess("Something went wrong!");
    }
  };

  const handleWebsiteToggle = async (item) => {
    if (can("city", "edit") === false) {
      alert("You don't have permission.");
      return;
    }

    if (item.status !== "Active") {
      alert("Inactive city cannot be enabled for Website. Please set Status to Active first.");
      return;
    }

    try {
      const response = await fetch(
        `${API_URL.replace(/\/$/, "")}/api/admin/city/update/${item._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            website: !item.website,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setCity((prev) =>
          prev.map((row) =>
            row._id === item._id
              ? { ...row, website: !item.website }
              : row
          )
        );
      } else {
        setSuccess(result.message || "Failed to update Website status.");
      }
    } catch (err) {
      console.error(err);
      setSuccess("Something went wrong!");
    }
  };

  const columns = cityColumns(handleEdit, handleDelete, pageSize, handleStatusToggle, handleWebsiteToggle);

  
  return (
    <div class="container body">
      <div class="main_container">
        <Sidebar />
        <Header pageTitle="City" />
        {/* page */}

        <div className="right_col" role="main">
          {/* top tiles */}
         
          {/* /top tiles */}
          {/* body card start */}
          <div className="x_panel">
            <div className="x_title">
              <h2> City Entry</h2>
              <ul className="nav navbar-right panel_toolbox" style={{ minWidth: 1 }}>
                <li>
                  <a className="collapse-link">
                    <i className="fa fa-chevron-up" />
                  </a>
                </li>
               
              </ul>
              <div className="clearfix" />
            </div>
            <div className="x_content">
              <div className="row">
                <div className="col-sm-12">
                  {/* filter start */}
                  <div className="filtercont">
                   <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                      <div className="col-12 col-sm-4 pr-lg-0 mb-3">
                        <label>Select Country</label>
                        <select
                          className={`form-select ${errors.country_id ? "is-invalid" : ""}`}
                          value={selectedCountry || ""}
                          {...register("country_id", {
                            required: "Please select a country",
                            onChange: handleCountryChange,
                          })}
                        >
                            <option value="">Select</option>
                            {Array.isArray(country) &&
                              country.map((item) => (
                                <option key={item._id} value={item._id}>
                                  {item.name}
                                </option>
                              ))}
                            
                          </select>
                          {errors.country_id && (
                            <div className="text-danger mt-1">{errors.country_id.message}</div>
                          )}
                      </div>
                      <div className="col-12" />
                     <div className="col-12 col-sm-4 mb-3">
                          <label>Select State</label>
                          <select
                            className={`form-select ${errors.state_id ? "is-invalid" : ""}`}
                            value={selectedState || ""}
                            {...register("state_id", { required: "Please select a state" })}
                          >
                            <option value="">Select</option>
                            {Array.isArray(state) &&
                              state.map((c) => (
                                <option key={c._id} value={c._id}>
                                  {c.name}
                                </option>
                              ))}
                          </select>
                          {errors.state_id && <div className="text-danger mt-1">{errors.state_id.message}</div>}
                        </div>
                      <div className="col-12" />
                      <div className="col-12 col-sm-4 pr-lg-0 mb-3">
                        <label>Enter City</label>
                        <input
                            type="text"
                            className={`form-control ${errors.name ? "is-invalid" : ""}`}
                            {...register("name", {
                              required: "Please enter city name",
                              minLength: { value: 2, message: "Minimum 2 characters" },
                            })}
                          />
                          {errors.name && (
                            <div className="text-danger mt-1">{errors.name.message}</div>
                          )}
                      </div>
                      <div className="col-12" />
                      <div className="col-12 col-sm-4 pr-lg-0">
                        <input type="checkbox" id="active" {...register("isActive")} />
                        <label htmlFor="active" className="form-label m-2">
                          All Platform Status
                        </label>
                      </div>
                      <div className="col-12 col-sm-4 pr-lg-0">
                        <input
                          type="checkbox"
                          id="website"
                          {...register("website")}
                          disabled={!isActive}
                        />
                        <label htmlFor="website" className="form-label m-2">
                          Website
                        </label>
                      </div>
                      <div className="col-12" />
                      <div className="col-12 col-sm-4 pr-lg-0 text-right">
                          <button type="submit" className="btn sitebtn" disabled={loading}>
                            {loading ? "Submitting..." : "Submit"}
                          </button>
                          {success && <div className="text-success mt-2">{success}</div>}
                          {errorMsg && <div className="text-danger mt-2">{errorMsg}</div>}
                        </div>
                    </div>
                    </form>
                  </div>
                  {/* filter end */}
                </div>
              </div>
            </div>
          </div>
          {/* bod card end */}
          {/* body card start */}
          <div className="x_panel">
            <div className="x_title">
              <h2>City List</h2>
              <ul className="nav navbar-right panel_toolbox" style={{ minWidth: 1 }}>
                <li>
                  <a className="collapse-link">
                    <i className="fa fa-chevron-up" />
                  </a>
                </li>
                
              </ul>
              <div className="clearfix" />
            </div>
            <div className="x_content">
              {/* Search & Filter Bar */}
              <div className="row mb-3">
                <div className="col-12 col-md-8 col-lg-6">
                  <form onSubmit={handleSearchSubmit}>
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: "10px" }}>
                      <div style={{ minWidth: "170px", flex: "1 1 auto", maxWidth: "220px" }}>
                        <select
                          className="form-select form-control"
                          value={selectedStateFilter}
                          onChange={handleStateFilterChange}
                          style={{ height: "38px", textTransform: "capitalize" }}
                        >
                          <option value="">All</option>
                          {Array.isArray(filterStateList) &&
                            filterStateList.map((item) => (
                              <option key={item._id} value={item._id} style={{ textTransform: "capitalize" }}>
                                {item.name}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="input-group" style={{ flex: "2 1 240px", minWidth: "220px" }}>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search city..."
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                        />
                        <button className="btn sitebtn mb-0" type="submit" style={{ borderRadius: "0 4px 4px 0" }}>
                          <i className="fa fa-search" /> Search
                        </button>
                        {(activeSearch || searchInput || selectedStateFilter) && (
                          <button
                            className="btn btn-secondary mb-0 ms-1 ml-1"
                            type="button"
                            onClick={handleSearchReset}
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              <div className="row">
                <div className="col-sm-12">
                  <AdminDataTable
                    columns={columns}
                    data={Array.isArray(city) ? city : []}
                    page={page}
                    pageSize={pageSize}
                    totalPages={totalPages}
                    loading={listLoading}
                    onPageChange={setPage}
                    onPageSizeChange={(size) => {
                      setPageSize(size);
                      setPage(1);
                      fetchCity(1, size, activeSearch, selectedStateFilter);
                    }}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* bod card end */}

        </div>

        {/* page end */}
        <Footer />

      </div>
    </div>
  );
}